import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Link,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import UserAvatar from '../../../../components/UserAvatar';
import {getExpertByUid as getStaticExpertByUid} from '../../../experts/expertsData';
import {formatDate} from '../../../../utils/formatTimestamp';
import SendMessageButton from '../../../../components/SendMessageButton';
import {useSession} from '../../../../firebase/UserProvider';
import {resolveExpertAvatarUrl} from '../../../../utils/expertAvatar';
import {Link as RouterLink} from 'react-router';
import {getCachedExpert, getCachedFintels} from './authorCache';
import {extractUid} from '../../../../firebase/firestore/chats';
import useChatStore from '../../../../stores/useChatStore';

const relevanceTooltip = 'Computed by AI. Takes into account AoR, comments, and papers authored. Not shared externally.';

const DetailRow = ({label, children}) => (
    <Stack spacing={0.5}>
        <Typography variant='overline' sx={{color: 'text.secondary', lineHeight: 1.2}}>{label}</Typography>
        <Typography variant='body1'>{children}</Typography>
    </Stack>
);

const ListAsChips = ({items}) => {
    const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!safeItems.length) return <Typography color='text.secondary'>—</Typography>;
    return (
        <Stack direction='row' gap={1} flexWrap='wrap'>
            {safeItems.map((item, idx) => (
                <Chip key={`${item}-${idx}`} label={item} variant='outlined'/>
            ))}
        </Stack>
    );
};

const asDisplayValue = (value) => {
    if (value === null || value === undefined) return '—';
    const s = String(value).trim();
    return s.length ? s : '—';
};

function formatRelativeTime(ts) {
    if (!ts) return null;
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
}

function formatShortDate(ts) {
    return formatDate(ts, '');
}

const formatPhone = (value) => {
    if (!value) return '—';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(value).trim();
};

const formatTsVoip = (value) => {
    if (!value) return '—';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 7) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return String(value).trim();
};

/**
 * Clickable author name as dashed underlined blue text
 * Opens a modal with expert details when clicked
 * @param {string} userId - The user ID
 * @param {string} userDisplayName - The user display name
 * @param {number} flex - Flex value for the container
 * @param {{ displayName, hasAvatar, profilePicture }|null} [avatarProfile] - Pre-resolved profile from report.authorProfiles
 * @param {string} [chatParentId] - When set (Collection assignee), chats opened from this modal are children of it
 */
const AuthorChip = ({userId, userDisplayName, flex = 1, avatarProfile, size = 'medium', chatParentId = null}) => {
    const dense = size === 'small';
    const {user} = useSession();
    const [open, setOpen] = useState(false);

    const staticExpert = useMemo(() => (userId ? getStaticExpertByUid(userId) : null), [userId]);
    const [firestoreExpert, setFirestoreExpert] = useState(undefined);
    const [expertLoading, setExpertLoading] = useState(false);

    // Author profile + recent FINTELs are only needed inside the dialog, so we
    // defer both network calls until it opens. This avoids one expert fetch and
    // one approved-FINTEL fetch per row on page load. Lookups go through a
    // shared module-level cache so the same UID across rows hits the network once.
    useEffect(() => {
        if (!open || !userId) return;
        let cancelled = false;

        setExpertLoading(true);
        getCachedExpert(userId, staticExpert?.handle)
            .then((data) => { if (!cancelled) setFirestoreExpert(data); })
            .catch((err) => {
                console.error('[AuthorChip] Failed to load expert', err);
                if (!cancelled) setFirestoreExpert(null);
            })
            .finally(() => { if (!cancelled) setExpertLoading(false); });

        return () => { cancelled = true; };
    }, [open, userId, staticExpert?.handle]);

    const expert = useMemo(() => {
        if (firestoreExpert) {
            return {...(staticExpert || {}), ...firestoreExpert};
        }
        return staticExpert || null;
    }, [firestoreExpert, staticExpert]);

    const [recentFintels, setRecentFintels] = useState(null);

    useEffect(() => {
        if (!open) {
            setRecentFintels(null);
            return;
        }
        const expertUid = firestoreExpert?.uid ?? staticExpert?.uid ?? userId;
        if (!expertUid || !user?.uid) return;
        let cancelled = false;
        getCachedFintels(expertUid)
            .then((all) => {
                if (cancelled) return;
                const accessible = all
                    .filter((r) => r.userId === user.uid || (r.shared_with || []).includes(user.uid))
                    .slice(0, 5);
                setRecentFintels(accessible);
            })
            .catch((err) => {
                console.error('[AuthorChip] Failed to load recent FINTELs', err);
                if (!cancelled) setRecentFintels([]);
            });
        return () => {
            cancelled = true;
        };
    }, [open, firestoreExpert?.uid, staticExpert?.uid, userId, user?.uid]);

    const headshotUrl = resolveExpertAvatarUrl({uid: userId, ...expert}) || expert?.photo;

    const handleClick = (e) => {
        e.stopPropagation();
        if (userId || staticExpert) {
            setOpen(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
        if (chatParentId) {
            useChatStore.getState().closeChatsByParent(chatParentId);
        }
    };

    const handleDialogClose = (event, reason) => {
        if (reason === 'backdropClick') {
            const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
            const clickedChat = path.some((node) => node?.dataset?.chatWindow != null)
                || event?.target?.closest?.('[data-chat-window]');
            if (clickedChat) return;
        }
        handleClose();
    };

    useEffect(() => {
        if (!chatParentId) return undefined;
        return () => {
            useChatStore.getState().closeChatsByParent(chatParentId);
        };
    }, [chatParentId]);

    // Clickable whenever we have something to look up; the expert profile is
    // fetched lazily once the dialog opens.
    const isClickable = !!(userId || staticExpert);
    const displayName = expert?.displayName || userDisplayName || 'Unknown';

    // Inline avatar — prefer pre-resolved avatarProfile from the report payload (no extra fetch),
    // fall back to the expert profile once the dialog has loaded it.
    const inlineAvatarUser = useMemo(() => {
        if (avatarProfile) {
            return {
                uid: userId,
                displayName: avatarProfile.displayName || userDisplayName,
                hasAvatar: avatarProfile.hasAvatar,
                profilePicture: avatarProfile.profilePicture,
            };
        }
        if (expert) {
            return {
                ...expert,
                uid: expert.uid || userId,
                profilePicture: expert.profilePicture || headshotUrl,
            };
        }
        // Fallback: initials only, no avatar fetch on page load.
        return { uid: userId, displayName: userDisplayName };
    }, [avatarProfile, expert, headshotUrl, userId, userDisplayName]);

    const avatarUser = useMemo(() => {
        if (!expert) return null;
        return {
            ...expert,
            profilePicture: expert.profilePicture || headshotUrl,
        };
    }, [expert, headshotUrl]);

    const chatUser = useMemo(() => {
        const uid = expert?.uid || (chatParentId ? userId : null);
        if (!uid) return null;
        const name = expert?.displayName || expert?.handle || userDisplayName;
        return {
            userDisplayName: name,
            displayName: name,
            headshot: headshotUrl,
            profilePicture: expert?.profilePicture || avatarProfile?.profilePicture || headshotUrl,
            handle: expert?.handle,
            agency: expert?.organization,
            office: expert?.office,
            poc: expert?.poc,
            contact: expert?.contact,
            title: expert?.title,
            email: expert?.email,
            uid,
            id: uid,
            userId: uid,
        };
    }, [expert, headshotUrl, chatParentId, userId, userDisplayName, avatarProfile?.profilePicture]);

    const sessionUid = extractUid(user);
    const canMessage = Boolean(chatUser && sessionUid && sessionUid !== extractUid(chatUser));
    const showSend = Boolean(canMessage && (expert?.uid || chatParentId));

    const paletteKey = expert?.relevance === 'high' ? 'success' : expert?.relevance === 'medium' ? 'info' : 'warning';
    const updatedAgo = formatRelativeTime(expert?.expertiseUpdatedAt);

    const twoCol = {
        direction: {xs: 'column', sm: 'row'},
        spacing: {xs: 1.5, sm: 3},
        alignItems: {xs: 'stretch', sm: 'flex-start'}
    };

    return (
        <>
            <Stack
                flex={flex}
                direction="row"
                alignItems="center"
                spacing={dense ? 0.5 : 1}
                sx={{ cursor: isClickable ? 'pointer' : 'default', minWidth: 0 }}
                onClick={isClickable ? handleClick : undefined}
            >
                <UserAvatar
                    user={inlineAvatarUser}
                    userId={userId}
                    sx={dense
                        ? {width: 18, height: 18, fontSize: '0.55rem', flexShrink: 0}
                        : {width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0}}
                />
                <Link
                    component="span"
                    sx={{
                        textAlign: 'left',
                        textDecoration: '1px dashed underline',
                        color: isClickable ? 'primary.main' : 'text.primary',
                        borderColor: 'primary.main',
                        cursor: isClickable ? 'pointer' : 'default',
                        width: 'fit-content',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...(dense ? {fontSize: '0.75rem', lineHeight: 1.2} : null),
                    }}
                >
                    {displayName}
                </Link>
            </Stack>

            {open && (
                <Dialog
                    open={open}
                    onClose={handleDialogClose}
                    maxWidth="md"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                maxHeight: '90vh',
                            }
                        }
                    }}
                >
                    {!expert && expertLoading && (
                        <DialogContent>
                            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{py: 6}}>
                                <CircularProgress size={28}/>
                                <Typography variant="body2" color="text.secondary">Loading profile…</Typography>
                            </Stack>
                        </DialogContent>
                    )}

                    {!expert && !expertLoading && (
                        <DialogContent>
                            <Stack spacing={2} sx={{py: 4}}>
                                <Typography variant="h6">{displayName}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No expert profile is available for this author.
                                </Typography>
                                {showSend && (
                                    <Stack direction="row" justifyContent="flex-start">
                                        <SendMessageButton isIcon={false} user={chatUser} parentId={chatParentId}/>
                                    </Stack>
                                )}
                                <Stack direction="row" justifyContent="flex-end">
                                    <Button onClick={handleClose}>Close</Button>
                                </Stack>
                            </Stack>
                        </DialogContent>
                    )}

                    {expert && (
                    <DialogContent sx={{p: 0}}>
                        <Stack p={3} spacing={3} overflow="auto">
                            <Paper elevation={0}
                                   sx={{p: 3, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`}}>
                                <Stack spacing={3}>
                                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={3} alignItems={'flex-start'}>
                                        <UserAvatar
                                            user={avatarUser}
                                            userId={expert.uid || userId}
                                            sx={{width: 96, height: 96, fontSize: '2rem'}}
                                        />
                                        <Stack spacing={0.75} flex={1}>
                                            <Typography variant='h5'
                                                        sx={{fontWeight: 700}}>{expert.displayName}</Typography>
                                            {(expert.title || expert.office) && (
                                                <Typography sx={{color: 'text.secondary'}}>
                                                    {asDisplayValue(expert.title)}{expert.office ? ` • ${asDisplayValue(expert.office)}` : ''}
                                                </Typography>
                                            )}
                                            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                                <Typography
                                                    sx={{color: 'text.secondary'}}>{expert.organization}</Typography>
                                                <Typography sx={{color: 'text.secondary'}}>•</Typography>
                                                <Box component="img" src={'/assets/jabber.png'} alt="Jabber"
                                                     sx={{width: 16, height: 16}}/>
                                                <Typography sx={{color: 'text.secondary'}}>{expert.handle}</Typography>
                                            </Stack>

                                            {showSend && (
                                                <Stack direction='row' gap={1} pt={1} flexWrap='wrap'>
                                                    <SendMessageButton isIcon={false} user={chatUser} parentId={chatParentId || undefined}/>
                                                </Stack>
                                            )}

                                            {expert.expertise && (
                                                <Stack spacing={0.5} pt={1}>
                                                    <Typography variant='body2'
                                                                sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                                        Expertise Summary
                                                    </Typography>
                                                    <Typography variant='body1'>{expert.expertise}</Typography>
                                                    <Typography variant='body2'
                                                                sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                                        Auto-generated from published fintel by this expert
                                                        {updatedAgo ? ` • Updated ${updatedAgo}` : ''}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Divider/>

                                    <Stack spacing={3}>
                                        <Stack spacing={3} direction={'row'}>
                                            <Stack spacing={1.5} flexGrow={1}>
                                                <Typography variant='subtitle1'
                                                            sx={{fontWeight: 700}}>Organization</Typography>
                                                <Stack spacing={2}>
                                                    <Stack {...twoCol}>
                                                        <DetailRow
                                                            label='Organization'>{asDisplayValue(expert.organization)}</DetailRow>
                                                        <DetailRow label='Office'>{asDisplayValue(expert.office)}</DetailRow>
                                                    </Stack>
                                                    <Stack {...twoCol}>
                                                        <DetailRow label='Location'>{asDisplayValue(expert.location)}</DetailRow>
                                                        <DetailRow
                                                            label='Clearance'>{asDisplayValue(expert.clearance)}</DetailRow>
                                                    </Stack>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant='overline'
                                                                    sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                                            Estimated Relevance
                                                        </Typography>
                                                        <Tooltip title={relevanceTooltip} placement='top'>
                                                            <Chip
                                                                label={expert.relevance}
                                                                sx={{textTransform: 'capitalize', width: 'fit-content'}}
                                                                icon={<Box sx={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: '50%',
                                                                    bgcolor: (theme) => theme.palette[paletteKey]?.main
                                                                }}/>}
                                                            />
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            </Stack>

                                            <Stack spacing={1.5} flexGrow={1}>
                                                <Typography variant='subtitle1'
                                                            sx={{fontWeight: 700}}>Contact</Typography>
                                                <Stack spacing={2}>
                                                    <DetailRow label='Email'>{asDisplayValue(expert.email)}</DetailRow>
                                                    <Stack {...twoCol}>
                                                        <DetailRow
                                                            label='Cell phone'>{formatPhone(expert.cellPhone)}</DetailRow>
                                                        <DetailRow
                                                            label='Landline'>{formatPhone(expert.landline)}</DetailRow>
                                                    </Stack>
                                                    <DetailRow label='TS-VoIP'>{formatTsVoip(expert.tsVoip)}</DetailRow>
                                                </Stack>
                                            </Stack>
                                        </Stack>

                                        <Stack spacing={1.5}>
                                            <Typography variant='subtitle1'
                                                        sx={{fontWeight: 700}}>Expertise</Typography>
                                            <DetailRow label='Area of Responsibility (AoR)'>
                                                {expert.arr?.length ? expert.arr.join(', ') : '—'}
                                            </DetailRow>
                                            <Stack spacing={0.75}>
                                                <Typography variant='overline' sx={{
                                                    color: 'text.secondary',
                                                    lineHeight: 1.2
                                                }}>Specialties</Typography>
                                                <ListAsChips items={expert.specialties}/>
                                            </Stack>
                                            <Stack spacing={0.75}>
                                                <Typography variant='overline' sx={{
                                                    color: 'text.secondary',
                                                    lineHeight: 1.2
                                                }}>Languages</Typography>
                                                <ListAsChips items={expert.languages}/>
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    <Divider/>

                                    <Stack spacing={1}>
                                        <Typography variant='h6'>Recent Contributions</Typography>
                                        {recentFintels === null && (
                                            <Typography variant='body2' color='text.secondary'>Loading…</Typography>
                                        )}
                                        {recentFintels !== null && recentFintels.length === 0 && (
                                            <Typography variant='body2' color='text.secondary'>No accessible FINTELs found.</Typography>
                                        )}
                                        {recentFintels !== null && recentFintels.length > 0 && (
                                            <Stack spacing={0.75}>
                                                {recentFintels.map((r) => (
                                                    <Stack key={r.id} direction='row' alignItems='baseline' spacing={1}>
                                                        <Link
                                                            component={RouterLink}
                                                            to={`/products/${r.id}`}
                                                            variant='body2'
                                                            sx={{
                                                                textDecoration: 'none',
                                                                color: 'primary.main',
                                                                '&:hover': {textDecoration: 'underline'},
                                                            }}
                                                        >
                                                            {r.title || r.meta?.title || 'Untitled'}
                                                        </Link>
                                                        {r.updatedAt && (
                                                            <Typography variant='caption' color='text.secondary' noWrap>
                                                                {formatShortDate(r.updatedAt)}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}
                                   flexWrap="wrap">
                                {expert.handle ? (
                                    <Button component={RouterLink} to={`/experts/${expert.handle}`} variant="outlined"
                                            size="small">
                                        Open full expert page
                                    </Button>
                                ) : (
                                    <span/>
                                )}
                                <Button onClick={handleClose}>Close</Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    )}
                </Dialog>
            )}
        </>
    );
};

export default AuthorChip;
